import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft, FiImage, FiTrash2 } from 'react-icons/fi';
import { apiJson, ApiError } from '@/lib/api';
import type { JournalEntryListItem } from '@/lib/types';

type PresignedUrlResponse = {
  presigned_url: string;
};

function formatEntryDate(iso: string): string {
  const d = new Date(iso + (iso.includes('T') ? '' : 'T12:00:00'));
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function isImageType(t: string): boolean {
  return t.startsWith('image/');
}

function AttachmentImage({ attachmentId }: { attachmentId: number }) {
  const { getToken } = useAuth();
  const urlQuery = useQuery({
    queryKey: ['journal-attachment-url', attachmentId],
    queryFn: async () =>
      (
        await apiJson<PresignedUrlResponse>(
          getToken,
          `/v1/journal/attachments/${attachmentId}/download`
        )
      ).presigned_url,
  });

  if (!urlQuery.data) {
    return (
      <div className='w-[100px] h-[100px] rounded-xl bg-gray-200 animate-pulse flex items-center justify-center'>
        <span className='text-xs text-gray-400'>…</span>
      </div>
    );
  }

  return (
    <img
      src={urlQuery.data}
      alt=''
      className='w-[100px] h-[100px] object-cover rounded-xl border border-gray-100'
    />
  );
}

function JournalEntryDetailPage() {
  const { entryId } = Route.useParams();
  const id = parseInt(entryId, 10);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const entryQuery = useQuery({
    queryKey: ['journal-entry', id],
    queryFn: async () => apiJson<JournalEntryListItem>(getToken, `/v1/journal/${id}`),
    enabled: Number.isFinite(id),
  });

  const entry = entryQuery.data;

  useEffect(() => {
    if (entry?.entry_type) {
      track('Journal Entry Viewed', { entry_type: entry.entry_type });
    }
  }, [entry?.entry_type]);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiJson(getToken, `/v1/journal/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      if (entry?.entry_type) {
        track('Journal Entry Deleted', { entry_type: entry.entry_type });
      }
      void queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      void queryClient.removeQueries({ queryKey: ['journal-entry', id] });
      navigate({ to: '/app/journal' });
    },
    onError: (err: unknown) => {
      let msg = 'Could not delete entry.';
      if (err instanceof ApiError && err.bodyText) {
        try {
          const o = JSON.parse(err.bodyText) as { detail?: string };
          if (o.detail) msg = o.detail;
        } catch {
          /* ignore */
        }
      }
      window.alert(msg);
    },
  });

  const confirmDelete = () => {
    if (
      !window.confirm(
        'Delete this entry? This cannot be undone. Your co-parent never saw this entry.'
      )
    ) {
      return;
    }
    deleteMutation.mutate();
  };

  if (!Number.isFinite(id)) {
    return (
      <div className='min-h-screen bg-sky-light p-6 text-center text-gray-500'>Invalid entry.</div>
    );
  }

  if (entryQuery.isLoading || !entry) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (entryQuery.isError) {
    return (
      <div className='min-h-screen bg-sky-light p-6 text-center'>
        <p className='text-gray-600'>Couldn&apos;t load this entry.</p>
        <Link to='/app/journal' className='text-primary font-semibold mt-4 inline-block'>
          Back to journal
        </Link>
      </div>
    );
  }

  const callTypeLabel =
    entry.call_type === 'FACETIME'
      ? 'FaceTime'
      : entry.call_type === 'VIDEO'
        ? 'Video call'
        : entry.call_type === 'REGULAR'
          ? 'Phone call'
          : entry.call_type;
  const directionLabel =
    entry.call_direction === 'INBOUND'
      ? 'Inbound'
      : entry.call_direction === 'OUTBOUND'
        ? 'Outbound'
        : entry.call_direction;

  const timeRange =
    entry.call_start_time && entry.call_end_time
      ? `${new Date(entry.call_start_time).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })} — ${new Date(entry.call_end_time).toLocaleString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        })}`
      : null;

  return (
    <div className='min-h-screen bg-sky-light pb-12'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-6 py-4'>
          <Link
            to='/app/journal'
            className='inline-flex items-center gap-2 text-primary hover:underline font-semibold'
            style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
          >
            <FiArrowLeft /> Back
          </Link>
        </div>
      </header>

      <div className='max-w-2xl mx-auto px-6 py-6 space-y-4'>
        <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-5'>
          <div className='inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-3'>
            {entry.entry_type === 'PHONE_CALL' ? (
              <span>Phone call</span>
            ) : (
              <span>General note</span>
            )}
          </div>
          <p className='text-sm text-gray-500 mb-4'>{formatEntryDate(entry.entry_date)}</p>
          {entry.title ? (
            <h1
              className='text-2xl font-bold text-gray-900 mb-2'
              style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
            >
              {entry.title}
            </h1>
          ) : null}

          {entry.entry_type === 'PHONE_CALL' &&
          (callTypeLabel || directionLabel || timeRange || entry.call_duration_minutes != null) ? (
            <div className='mt-4 rounded-xl border border-primary/10 bg-primary/[0.04] divide-y divide-primary/10'>
              {callTypeLabel ? (
                <div className='p-3 flex gap-3'>
                  <span className='text-xs font-bold uppercase tracking-wide text-gray-400 w-24 shrink-0'>
                    Type
                  </span>
                  <span className='text-gray-900'>{callTypeLabel}</span>
                </div>
              ) : null}
              {directionLabel ? (
                <div className='p-3 flex gap-3'>
                  <span className='text-xs font-bold uppercase tracking-wide text-gray-400 w-24 shrink-0'>
                    Direction
                  </span>
                  <span className='text-gray-900'>{directionLabel}</span>
                </div>
              ) : null}
              {timeRange ? (
                <div className='p-3 flex gap-3'>
                  <span className='text-xs font-bold uppercase tracking-wide text-gray-400 w-24 shrink-0'>
                    Time
                  </span>
                  <span className='text-gray-900'>{timeRange}</span>
                </div>
              ) : null}
              {entry.call_duration_minutes != null ? (
                <div className='p-3 flex gap-3'>
                  <span className='text-xs font-bold uppercase tracking-wide text-gray-400 w-24 shrink-0'>
                    Duration
                  </span>
                  <span className='text-gray-900'>{entry.call_duration_minutes} minutes</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {entry.body ? (
            <>
              <hr className='my-4 border-gray-100' />
              <p
                className='text-gray-800 whitespace-pre-wrap leading-relaxed'
                style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
              >
                {entry.body}
              </p>
            </>
          ) : null}
        </div>

        {entry.attachments?.length ? (
          <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-5'>
            <div className='flex items-center gap-2 mb-4 text-gray-900 font-bold'>
              <FiImage className='text-primary' />
              Attachments
            </div>
            <div className='flex flex-wrap gap-3'>
              {entry.attachments.map((a) =>
                isImageType(a.file_type) ? (
                  <div key={a.id}>
                    <AttachmentImage attachmentId={a.id} />
                  </div>
                ) : (
                  <div
                    key={a.id}
                    className='w-[100px] h-[100px] rounded-xl border border-gray-100 bg-gray-50 flex flex-col items-center justify-center p-2'
                  >
                    <span className='text-[10px] text-gray-500 text-center line-clamp-3'>{a.name}</span>
                  </div>
                )
              )}
            </div>
          </div>
        ) : null}

        <div className='flex items-center gap-2 text-sm text-gray-400 px-1'>
          <span>Saved {new Date(entry.created_at).toLocaleString('en-US')}</span>
        </div>

        <button
          type='button'
          onClick={confirmDelete}
          disabled={deleteMutation.isPending}
          className='w-full flex items-center justify-center gap-2 py-4 rounded-2xl border border-red-200 bg-white text-red-600 font-bold hover:bg-red-50 disabled:opacity-60'
          style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
        >
          <FiTrash2 />
          {deleteMutation.isPending ? 'Deleting…' : 'Delete entry'}
        </button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/journal/$entryId')({
  component: JournalEntryDetailPage,
});
